import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Bot, MessageSquare, AlertTriangle, Users2, Sparkles, ExternalLink, RefreshCw } from "lucide-react";

const FILTERS = [
  { k: "all", l: "Todas" },
  { k: "escalated", l: "Escaladas" },
  { k: "with_tools", l: "Con tools" },
  { k: "with_leads", l: "Con leads" },
];

export default function StaffAsistente() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [convs, setConvs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filter === "escalated") params.escalated = true;
    if (filter === "with_tools") params.has_tools = true;
    if (filter === "with_leads") params.has_leads = true;
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/agent/conversations", { params }),
        api.get("/admin/agent/stats"),
      ]);
      setConvs(c.data.items || []);
      setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold inline-flex items-center gap-1">
              <Bot className="w-3 h-3" /> Asistente IA
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Conversaciones del Asesor DCC</h1>
            <p className="mt-1 text-sm text-gray-400">Auditoría de conversaciones del agente conversacional.</p>
          </div>
          <button onClick={load} disabled={loading}
            data-testid="agent-refresh-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:bg-white/5">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="agent-stats">
            <StatCard label="Conversaciones 7d" value={stats.conversations} />
            <StatCard label="Mensajes 7d" value={stats.messages} />
            <StatCard label="Escaladas" value={stats.escalated} accent="amber" />
            <StatCard label="Leads generados" value={stats.leads_generated} accent="emerald" />
            <StatCard label="Tasa escalación" value={`${Math.round((stats.escalation_rate || 0) * 100)}%`} />
          </div>
        )}
        {stats?.top_tools?.length > 0 && (
          <div className="mt-4 p-3 bg-[#0F172A] border border-gray-800 rounded-xl">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Top tools 7d</p>
            <div className="flex flex-wrap gap-1.5">
              {stats.top_tools.map((t) => (
                <span key={t.tool} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F26A21]/20 text-[#F26A21] border border-[#F26A21]/40 font-mono">
                  {t.tool} · {t.count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              data-testid={`agent-filter-${f.k}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === f.k
                  ? "bg-[#F26A21] border-[#F26A21] text-[#0B0B0B] font-semibold"
                  : "border-gray-700 text-gray-300 hover:bg-white/5"
              }`}>
              {f.l}
            </button>
          ))}
        </div>

        {/* Conversations list */}
        <div className="mt-4 overflow-x-auto border border-gray-800 rounded-xl">
          <table className="w-full text-sm" data-testid="agent-conversations-table">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Usuario / Sesión</th>
                <th className="text-left px-3 py-2">Última actividad</th>
                <th className="text-center px-3 py-2">Msgs</th>
                <th className="text-center px-3 py-2">Tools</th>
                <th className="text-center px-3 py-2">Leads</th>
                <th className="text-center px-3 py-2">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {convs.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">Sin conversaciones.</td></tr>
              )}
              {convs.map((c) => (
                <tr key={c.id} className="border-t border-gray-800 hover:bg-white/5"
                    data-testid={`agent-conv-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <p className="text-white text-sm">{c.user_email || <span className="text-gray-500 italic">anónimo</span>}</p>
                    <p className="text-[10px] font-mono text-gray-500">{(c.session_id || c.id).slice(0, 24)}…</p>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {c.last_message_at ? new Date(c.last_message_at).toLocaleString("es-MX") : "—"}
                    {c.source_page && <p className="text-[9px] text-gray-500">{c.source_page}</p>}
                  </td>
                  <td className="px-3 py-2 text-center text-white">{c.messages_count}</td>
                  <td className="px-3 py-2 text-center">
                    {c.tools_used_count > 0
                      ? <span className="text-[#F26A21] text-xs font-semibold">{c.tools_used_count}</span>
                      : <span className="text-gray-600 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.leads_generated > 0
                      ? <span className="text-emerald-400 text-xs font-semibold">{c.leads_generated}</span>
                      : <span className="text-gray-600 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.escalated && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">
                        <AlertTriangle className="w-2.5 h-2.5" /> Escalada
                      </span>
                    )}
                    {!c.escalated && c.state === "active" && <span className="text-[9px] text-gray-500 uppercase">Activa</span>}
                    {!c.escalated && c.state === "closed" && <span className="text-[9px] text-gray-500 uppercase">Cerrada</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`${superadmin ? "/app/superadmin" : "/app/staff"}/asistente/${c.id}`}
                      data-testid={`agent-conv-open-${c.id}`}
                      className="text-[#93C5FD] hover:text-white text-xs inline-flex items-center gap-1">
                      Ver <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </StaffLayout>
  );
}

function StatCard({ label, value, accent }) {
  const color = accent === "amber" ? "text-amber-300"
              : accent === "emerald" ? "text-emerald-300"
              : "text-white";
  return (
    <div className="p-4 bg-[#0F172A] border border-gray-800 rounded-xl">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
      <p className={`mt-1 text-2xl font-bold font-mono ${color}`}>{value}</p>
    </div>
  );
}
